/** @format */

/* eslint react-hooks/rules-of-hooks: "warn", @typescript-eslint/prefer-nullish-coalescing: "warn" -- Legacy code: do not use as an example. */
import isPropValid from '@emotion/is-prop-valid';
import {
    Button,
    ButtonSize,
    ButtonVariant,
} from '@mpb-com/feather/components/atoms/button';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import {
    Icon,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import { useTranslation } from 'next-i18next';
import React, { Ref, forwardRef } from 'react';
import styled from 'styled-components';

import { SearchFacet } from '@toucan/common/services/search/constants';

import CloseIcon from '@toucan/client/global/icons/close.svg';

import { Link, LinkSize } from '@toucan/client/shared/components/link';

import { SearchFilterMenuOptionList } from '@toucan/client/features/search/components/model-search-results/components/filters/components/option-list';
import { SearchFilterMenuPriceOptions } from '@toucan/client/features/search/components/model-search-results/components/filters/components/price-options';

import { styles } from './search-filter-menu.styles';
import { SearchFilterDropdownProps } from './types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Legacy code: do not use as an example.
const DivWithRef = (props: { children: any }, ref: Ref<HTMLElement>) => {
    return React.createElement('div', { ref, ...props });
};

// eslint-disable-next-line @mpb-com/mpb/require-constants-file -- Legacy code: do not use as an example.
export const DEFAULT_TEST_ID = 'search-filter-menu';

const blacklistedProps = ['atScrollEnd', 'filter'];
const StyledSearchFilterDropdown = styled(forwardRef(DivWithRef)).withConfig({
    shouldForwardProp: (prop) =>
        isPropValid(prop) && !blacklistedProps.includes(prop),
})`
    ${styles}
`;

const SearchFilterDropdownWithRef = (
    {
        'data-testid': dataTestId = DEFAULT_TEST_ID,
        selectedItems = [],
        setSelectedItems,
        isLoading = false,
        filter,
        applyLabel,
        cancelLabel,
        clearLabel,
        onApply,
        onCancel,
        onClear,
    }: SearchFilterDropdownProps,
    ref: Ref<HTMLElement>
) => {
    const { t } = useTranslation();

    const numberSelected = selectedItems.length;
    const shouldRenderHeader = filter.id !== SearchFacet.productPriceModifier;

    function renderFilterOptions() {
        if (filter.id === SearchFacet.productPrice) {
            return (
                <SearchFilterMenuPriceOptions
                    filter={filter}
                    onClear={onClear}
                    isLoading={isLoading}
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                />
            );
        }

        return (
            <SearchFilterMenuOptionList
                filter={filter}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                isLoading={isLoading}
            />
        );
    }

    return (
        <StyledSearchFilterDropdown data-testid={dataTestId} ref={ref}>
            <Icon
                title={cancelLabel ?? ''}
                aria-label={cancelLabel ?? ''}
                onClick={onCancel}
                variant={IconVariant.NAVIGATION}
                size={IconSize.S}
                data-testid="filter-close-button"
                className="cancel"
            >
                <CloseIcon />
            </Icon>
            {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
            {shouldRenderHeader && (
                <div className="header">
                    {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                    {filter?.label && (
                        <Heading size={HeadingSize.H4}>{filter.label}</Heading>
                    )}
                    <span className="selected-count">
                        {t('{{count}} selected', { count: numberSelected })}
                    </span>
                    <Link
                        size={LinkSize.SMALL}
                        onClick={onClear}
                        className="clear"
                        data-testid={`${dataTestId}__clear-button`}
                    >
                        {clearLabel}
                    </Link>
                </div>
            )}
            {renderFilterOptions()}
            <div className="footer">
                <Button
                    variant={ButtonVariant.TERTIARY}
                    size={ButtonSize.SMALL}
                    onClick={onCancel}
                    data-testid={`${dataTestId}__cancel-button`}
                >
                    {cancelLabel}
                </Button>
                <Button
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.SMALL}
                    onClick={onApply}
                    data-testid={`${dataTestId}__apply-button`}
                >
                    {applyLabel}
                </Button>
            </div>
        </StyledSearchFilterDropdown>
    );
};

export const SearchFilterDropdown = forwardRef(SearchFilterDropdownWithRef);
